import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate, api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  useApi, post, put, Panel, PageHead, Stat, QuickForm, DataTable, StatusBadge, Tabs, inr,
} from "@/crm/ui";

const TABS = ["Overview", "Assignment", "Statement Analysis", "Bank Applications",
  "Credit & GST", "Documents & Consents", "Audit History"];

const tat = (from, to) => {
  if (!from || !to) return "—";
  const d = Math.round((new Date(to) - new Date(from)) / 86400000);
  return Number.isNaN(d) ? "—" : `${d}d`;
};

export default function LeadDetail() {
  const { leadId } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("Overview");
  const [form, setForm] = useState({});
  const [appForm, setAppForm] = useState({});
  const [statusRow, setStatusRow] = useState(null);
  const [statusForm, setStatusForm] = useState({});
  const [noteRow, setNoteRow] = useState(null);
  const [noteForm, setNoteForm] = useState({ note_type: "Query", note: "" });

  const detail = useApi(`/crm/leads/${leadId}`, [leadId]);
  const options = useApi("/crm/options");
  const analyses = useApi("/analyses");
  const meta = useApi("/crm/meta");
  const audit = useApi(tab === "Audit History" ? `/crm/audit?entity_id=${leadId}` : null, [tab, leadId]);
  const bankId = appForm.bank_id;
  const locations = useApi(bankId ? `/crm/banks/${bankId}/locations` : null, [bankId]);
  const managers = useApi(bankId ? `/crm/banks/${bankId}/managers` : null, [bankId]);
  const bankProducts = useApi(bankId ? `/crm/banks/${bankId}` : null, [bankId]);

  const d = detail.data;
  const opt = (rows, idKey, labelKey) =>
    (rows || []).map((r) => ({ value: r[idKey], label: `${r[labelKey]} (${r[idKey]})` }));

  if (!d) return <div className="text-sm text-muted-foreground" data-testid="lead-loading">Loading lead…</div>;
  const { lead, applications, statements, documents, consents } = d;
  const snap = lead.financial_snapshot;

  const changeStatus = async () => {
    const r = await put(`/crm/applications/${statusRow.application_id}/status`, statusForm,
      "Application status updated");
    if (r) {
      setStatusRow(null); setStatusForm({}); detail.reload();
      if (r.payout) setTimeout(() => nav("/crm/payouts"), 800);
    }
  };

  const addNote = async () => {
    const r = await post(`/crm/applications/${noteRow.application_id}/notes`, noteForm, "Note added");
    if (r) { setNoteRow(null); setNoteForm({ note_type: "Query", note: "" }); detail.reload(); }
  };

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      <Button variant="ghost" onClick={() => nav("/crm/pipeline")} data-testid="back-to-pipeline-btn">
        <ArrowLeft className="mr-2 h-4 w-4" /> All leads
      </Button>
      <PageHead eyebrow={`Lead · ${lead.lead_id}`} title={lead.customer_name}
                subtitle={`${lead.mobile || "—"} · ${lead.city || "—"} · Source: ${lead.source}`}
                actions={<StatusBadge value={lead.stage} />} />

      <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-6">
        <Stat label="Requirement" value={inr(lead.loan_amount)} testid="ld-requirement" />
        <Stat label="Bank Applications" value={applications.length} testid="ld-applications" />
        <Stat label="Approved" value={inr(applications.reduce((s, a) => s + (a.approved_amount || 0), 0))} testid="ld-approved" />
        <Stat label="Disbursed" value={inr(applications.reduce((s, a) => s + (a.disbursed_amount || 0), 0))} testid="ld-disbursed" />
        <Stat label="Statements" value={statements.length} testid="ld-statements" />
        <Stat label="Documents" value={documents.length} testid="ld-documents" />
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} testid="lead-tabs" />

      {tab === "Overview" && (
        <Panel title="Lead information" testid="lead-overview">
          <div className="grid gap-3 text-sm sm:grid-cols-3">
            {[["Lead ID", lead.lead_id], ["Customer", lead.customer_name], ["Mobile", lead.mobile],
              ["Email", lead.email], ["PAN", lead.pan], ["GSTIN", lead.gstin],
              ["City", lead.city], ["State", lead.state], ["Requirement", inr(lead.loan_amount)],
              ["Source", lead.source], ["Sub-source", lead.sub_source],
              ["Employee", lead.employee_id], ["Partner", lead.partner_id],
              ["Preferred Bank", lead.bank_id], ["Preferred Product", lead.product_id],
              ["Stage", lead.stage], ["Status", lead.status],
              ["Next follow-up", lead.next_followup], ["Created", fmtDate(lead.created_at)],
              ["Created by", lead.created_by], ["Remarks", lead.remarks]].map(([k, v]) => (
              <p key={k}><span className="text-muted-foreground">{k}: </span><b>{v || "—"}</b></p>))}
          </div>
        </Panel>
      )}

      {tab === "Assignment" && (
        <>
          <Panel title="Assign / reassign" testid="assignment-panel">
            <QuickForm testid="assignment-form" values={form} submitLabel="Save assignment"
                       onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))}
                       fields={[
                         { key: "employee_id", label: "Employee", options: opt(options.data?.employees, "employee_id", "employee_name") },
                         { key: "partner_id", label: "Partner", options: opt(options.data?.partners, "partner_id", "partner_name") },
                         { key: "bank_id", label: "Bank", options: opt(options.data?.banks, "bank_id", "bank_name") },
                         { key: "location_id", label: "Bank Location", options: opt((options.data?.locations || []).filter((l) => !form.bank_id || l.bank_id === form.bank_id), "location_id", "location_name") },
                         { key: "product_id", label: "Product", options: opt(options.data?.products, "product_id", "product_name") },
                         { key: "stage", label: "Stage", options: ["Lead", "Qualified", "Documents", "Login", "Sanction", "Disbursed", "Lost"] },
                         { key: "next_followup", label: "Next Follow-up", type: "date" },
                         { key: "remarks", label: "Remarks" },
                       ]}
                       onSubmit={async () => {
                         const r = await put(`/crm/leads/${leadId}`, form, "Lead assignment updated");
                         if (r) { setForm({}); detail.reload(); }
                       }} />
          </Panel>
          <Panel title="Assignment history" testid="assignment-history-panel">
            <DataTable testid="assignment-history-table" rows={lead.ownership_history}
                       columns={[{ key: "employee_id", label: "Assigned Employee" },
                         { key: "partner_id", label: "Assigned Partner" },
                         { key: "assigned_by", label: "Assigned By" },
                         { key: "at", label: "Date / Time", render: (r) => (r.at || "").replace("T", " ").slice(0, 19) }]} />
          </Panel>
        </>
      )}

      {tab === "Statement Analysis" && (
        <>
          <Panel title="Link an existing bank statement analysis" testid="link-statement-panel"
                 aside={<Link to="/analyzer" className="text-xs font-semibold text-primary" data-testid="open-analyzer-link">
                   Run a new analysis →</Link>}>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Ready analyses</Label>
                <select className="h-9 min-w-[280px] rounded-md border border-input bg-white px-2 text-sm"
                        data-testid="statement-select" value={form.job_id || ""}
                        onChange={(e) => setForm((f) => ({ ...f, job_id: e.target.value }))}>
                  <option value="">Select an analysis…</option>
                  {(analyses.data || []).filter((a) => a.status === "ready").map((a) => (
                    <option key={a.job_id} value={a.job_id}>
                      {(a.file_names || [])[0] || a.job_id} · {a.bank_name || "Bank"} · {a.transaction_count} txns
                    </option>))}
                </select>
              </div>
              <Button disabled={!form.job_id} data-testid="link-statement-btn"
                      onClick={async () => {
                        const r = await post(`/crm/leads/${leadId}/link-statement`,
                          { job_id: form.job_id }, "Statement analysis linked");
                        if (r) { setForm({}); detail.reload(); }
                      }}>Link analysis</Button>
            </div>
          </Panel>

          {snap && (
            <Panel title="Banking summary (from linked analysis)" testid="banking-summary-panel"
                   aside={<Button size="sm" variant="outline" className="bg-white"
                                  onClick={() => nav(`/workspace/${snap.job_id}`)}
                                  data-testid="open-full-analysis-btn">
                     Open full analysis <ExternalLink className="ml-2 h-3 w-3" /></Button>}>
              <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-5">
                <Stat label="Total Credits" value={inr(snap.total_credits)} testid="bs-credits" />
                <Stat label="Total Debits" value={inr(snap.total_debits)} testid="bs-debits" />
                <Stat label="Avg Monthly Credit" value={inr(snap.average_monthly_credits)} testid="bs-avg-credit" />
                <Stat label="Obligations (EMI)" value={inr(snap.estimated_monthly_emi)} testid="bs-emi" />
                <Stat label="Cash-flow Stability" value={snap.cash_flow_stability ?? "—"} testid="bs-stability" />
              </div>
              <p className="mt-3 text-xs text-muted-foreground">Linked on {fmtDate(snap.linked_at)} · Job {snap.job_id}</p>
            </Panel>
          )}

          <Panel title={`Linked analyses (${statements.length})`} testid="linked-statements-panel">
            <DataTable testid="linked-statements-table" rows={statements}
                       columns={[{ key: "job_id", label: "Job" },
                         { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                         { key: "bank", label: "Bank", render: (r) => r.statement?.bank_name || "—" },
                         { key: "credits", label: "Total Credits", right: true, render: (r) => inr(r.analysis?.summary?.total_credits) },
                         { key: "debits", label: "Total Debits", right: true, render: (r) => inr(r.analysis?.summary?.total_debits) },
                         { key: "avg", label: "Avg Monthly Credit", right: true, render: (r) => inr(r.analysis?.summary?.average_monthly_credits) },
                         { key: "emi", label: "Obligations", right: true, render: (r) => inr(r.analysis?.summary?.estimated_monthly_emi) },
                         { key: "created_at", label: "Analysis Date", render: (r) => fmtDate(r.created_at) },
                         { key: "open", label: "", render: (r) => (
                           <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                   data-testid={`open-analysis-${r.job_id}`}
                                   onClick={() => nav(`/workspace/${r.job_id}`)}>Open</Button>) }]} />
          </Panel>
        </>
      )}

      {tab === "Bank Applications" && (
        <>
          <Panel title="Log this lead into a bank" testid="create-application-panel"
                 aside={<span className="text-xs text-muted-foreground">One lead can be logged into multiple banks</span>}>
            <QuickForm testid="lead-application-form" values={appForm} submitLabel="Create application"
                       onChange={(k, v) => setAppForm((f) => ({ ...f, [k]: v }))}
                       fields={[
                         { key: "bank_id", label: "Bank", options: opt(options.data?.banks, "bank_id", "bank_name") },
                         { key: "location_id", label: "Bank Location", options: opt(locations.data, "location_id", "location_name") },
                         { key: "product_id", label: "Bank Product (mapped only)", options: (bankProducts.data?.products || []).map((p) => ({ value: p.product_id, label: p.product_name })) },
                         { key: "manager_id", label: "Bank Manager", options: opt(managers.data, "manager_id", "manager_name") },
                         { key: "employee_id", label: "Employee", options: opt(options.data?.employees, "employee_id", "employee_name") },
                         { key: "partner_id", label: "Partner", options: opt(options.data?.partners, "partner_id", "partner_name") },
                         { key: "application_number", label: "Bank Application Number" },
                         { key: "login_amount", label: "Login Amount", type: "number" },
                         { key: "login_date", label: "Login Date", type: "date" },
                         { key: "remarks", label: "Remarks" },
                       ]}
                       onSubmit={async () => {
                         const r = await post("/crm/applications", { ...appForm, lead_id: leadId },
                           "Application created");
                         if (r) { setAppForm({}); detail.reload(); }
                       }} />
          </Panel>
          <Panel title={`Bank applications (${applications.length})`} testid="bank-applications-panel">
            <DataTable testid="lead-applications-table" rows={applications}
                       empty="No bank applications yet for this lead."
                       columns={[{ key: "application_id", label: "Application ID" },
                         { key: "bank_name", label: "Bank" }, { key: "location_name", label: "Location" },
                         { key: "product_name", label: "Product" }, { key: "manager_id", label: "Manager" },
                         { key: "employee_id", label: "Employee" }, { key: "partner_id", label: "Partner" },
                         { key: "login_amount", label: "Login", right: true, render: (r) => inr(r.login_amount) },
                         { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                         { key: "approved_amount", label: "Approved", right: true, render: (r) => inr(r.approved_amount) },
                         { key: "disbursed_amount", label: "Disbursed", right: true, render: (r) => inr(r.disbursed_amount) },
                         { key: "login_date", label: "Login Date", render: (r) => fmtDate(r.login_date) },
                         { key: "approval_date", label: "Approval Date", render: (r) => fmtDate(r.approval_date) },
                         { key: "disbursement_date", label: "Disb. Date", render: (r) => fmtDate(r.disbursement_date) },
                         { key: "tat", label: "TAT", render: (r) => tat(r.login_date, r.disbursement_date || r.approval_date) },
                         { key: "actions", label: "Actions", render: (r) => (
                           <span className="flex gap-1">
                             <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                     data-testid={`update-status-${r.application_id}`}
                                     onClick={() => { setStatusRow(r); setStatusForm({ status: r.status }); }}>
                               Status</Button>
                             <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                     data-testid={`add-note-${r.application_id}`}
                                     onClick={() => setNoteRow(r)}>Query / Remark</Button>
                           </span>) }]} />
          </Panel>
          <Panel title="Status history" testid="status-history-panel">
            <DataTable testid="status-history-table"
                       rows={applications.flatMap((a) => (a.status_history || []).map((h) => ({ ...h, application_id: a.application_id })))}
                       columns={[{ key: "application_id", label: "Application" },
                         { key: "previous_status", label: "Old Status" },
                         { key: "new_status", label: "New Status", render: (r) => <StatusBadge value={r.new_status} /> },
                         { key: "user", label: "User" },
                         { key: "at", label: "Date / Time", render: (r) => (r.at || "").replace("T", " ").slice(0, 19) },
                         { key: "remarks", label: "Remarks" }]} />
          </Panel>
        </>
      )}

      {tab === "Credit & GST" && (
        <>
          <Panel title="External checks (CIBIL / GST / KYC)" testid="external-checks-panel"
                 aside={<Link to="/crm/integrations" className="text-xs font-semibold text-primary"
                              data-testid="open-integrations-link">Configure providers →</Link>}>
            <p className="mb-4 text-xs text-muted-foreground">
              Checks require an active customer consent and a configured provider. No simulated
              bureau or GST data is ever displayed, and no application is auto-approved or
              auto-rejected from these analyses.
            </p>
            <div className="flex flex-wrap gap-2">
              {[["Credit Bureau", "bureau-check", "Credit Bureau"],
                ["GST", "gst-check", "GST"], ["KYC", "kyc-check", "KYC"]].map(([label, path, ctype]) => (
                <span key={path} className="flex items-center gap-1">
                  <Button size="sm" variant="outline" className="bg-white"
                          data-testid={`run-${path}`}
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/crm/leads/${leadId}/${path}`);
                              toast.success(`${label} report fetched (${data.provider})`);
                              detail.reload();
                            } catch (e) { toast.error(errMsg(e)); }
                          }}>Run {label} check</Button>
                  <Button size="sm" variant="ghost" data-testid={`consent-${path}`}
                          onClick={async () => {
                            await post("/crm/consents", { lead_id: leadId, consent_type: ctype,
                              customer_name: lead.customer_name, status: "Active",
                              purpose: "Loan eligibility assessment" }, `${ctype} consent captured`);
                            detail.reload();
                          }}>+ consent</Button>
                </span>))}
            </div>
          </Panel>
          {["credit_bureau_summary", "gst_summary", "kyc_summary"].filter((k) => lead[k]).map((k) => (
            <Panel key={k} title={k.replace(/_/g, " ")} testid={`${k}-panel`}>
              <div className="grid gap-3 text-sm sm:grid-cols-3">
                {Object.entries(lead[k]).map(([kk, v]) => (
                  <p key={kk}><span className="text-muted-foreground">{kk.replace(/_/g, " ")}: </span>
                    <b>{String(v ?? "—")}</b></p>))}
              </div>
            </Panel>))}
          <Panel title={`Consents (${consents.length})`} testid="credit-consents-panel">
            <DataTable testid="credit-consents-table" rows={consents}
                       columns={[{ key: "consent_id", label: "Consent ID" },
                         { key: "consent_type", label: "Type" }, { key: "purpose", label: "Purpose" },
                         { key: "captured_by", label: "Captured By" },
                         { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                         { key: "consent_date", label: "Date", render: (r) => fmtDate(r.consent_date) }]} />
          </Panel>
        </>
      )}

      {tab === "Documents & Consents" && (
        <>
          <Panel title={`Documents (${documents.length})`} testid="lead-documents-panel"
                 aside={<Link to="/documents" className="text-xs font-semibold text-primary" data-testid="upload-doc-link">
                   Upload in Documents →</Link>}>
            <DataTable testid="lead-documents-table" rows={documents}
                       columns={[{ key: "file_name", label: "File" }, { key: "doc_type", label: "Type" },
                         { key: "size", label: "Size", right: true },
                         { key: "created_at", label: "Uploaded", render: (r) => fmtDate(r.created_at) }]} />
          </Panel>
          <Panel title="Consents (CIBIL / GST / KYC)" testid="lead-consents-panel"
                 aside={<Button size="sm" variant="outline" className="bg-white" data-testid="capture-consent-btn"
                                onClick={async () => {
                                  await post("/crm/consents", { lead_id: leadId, customer_name: lead.customer_name, consent_type: "Credit Bureau", purpose: "Loan eligibility assessment" }, "Consent captured");
                                  detail.reload();
                                }}>Capture bureau consent</Button>}>
            <DataTable testid="lead-consents-table" rows={consents}
                       columns={[{ key: "consent_id", label: "Consent ID" }, { key: "consent_type", label: "Type" },
                         { key: "purpose", label: "Purpose" }, { key: "captured_by", label: "Captured By" },
                         { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                         { key: "consent_date", label: "Date", render: (r) => fmtDate(r.consent_date) }]} />
            <p className="mt-3 text-xs text-muted-foreground">
              External bureau / GST providers are not configured — consent capture and the provider
              adapter are in place, no external calls are made.
            </p>
          </Panel>
        </>
      )}

      {tab === "Audit History" && (
        <Panel title={`Audit history (${audit.data?.total ?? 0})`} testid="lead-audit-panel">
          <DataTable testid="lead-audit-table" rows={audit.data?.records}
                     columns={[{ key: "created_at", label: "Date / Time", render: (r) => (r.created_at || "").replace("T", " ").slice(0, 19) },
                       { key: "user_email", label: "User" }, { key: "action_type", label: "Action" },
                       { key: "entity", label: "Entity" }, { key: "statement_id", label: "Entity ID" },
                       { key: "field", label: "Field" }, { key: "previous_value", label: "Old Value" },
                       { key: "new_value", label: "New Value" }, { key: "reason", label: "Reason" }]} />
        </Panel>
      )}

      <Dialog open={!!statusRow} onOpenChange={() => setStatusRow(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="status-dialog">
          <DialogHeader><DialogTitle>Update status — {statusRow?.application_id}</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">New status</Label>
              <select className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm"
                      data-testid="status-select" value={statusForm.status || ""}
                      onChange={(e) => setStatusForm((f) => ({ ...f, status: e.target.value }))}>
                {(meta.data?.application_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {["Approved", "Part Approved", "Sanctioned"].includes(statusForm.status) && (
              <div className="space-y-1">
                <Label className="text-xs">Approved amount</Label>
                <Input type="number" data-testid="approved-amount-input" value={statusForm.approved_amount || ""}
                       onChange={(e) => setStatusForm((f) => ({ ...f, approved_amount: e.target.value }))} />
              </div>)}
            {statusForm.status === "Disbursed" && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs">Disbursed amount</Label>
                  <Input type="number" data-testid="disbursed-amount-input" value={statusForm.disbursed_amount || ""}
                         onChange={(e) => setStatusForm((f) => ({ ...f, disbursed_amount: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Disbursement date</Label>
                  <Input type="date" data-testid="disbursement-date-input" value={statusForm.disbursement_date || ""}
                         onChange={(e) => setStatusForm((f) => ({ ...f, disbursement_date: e.target.value }))} />
                </div>
              </>)}
            {statusForm.status === "Hold" && (
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs">Hold reason</Label>
                <Input data-testid="hold-reason-input" value={statusForm.hold_reason || ""}
                       onChange={(e) => setStatusForm((f) => ({ ...f, hold_reason: e.target.value }))} />
              </div>)}
            {statusForm.status === "Rejected" && (
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs">Rejection reason</Label>
                <select className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm"
                        data-testid="rejection-reason-select" value={statusForm.rejection_reason || ""}
                        onChange={(e) => setStatusForm((f) => ({ ...f, rejection_reason: e.target.value }))}>
                  <option value="">Select…</option>
                  {(meta.data?.rejection_reasons || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>)}
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">Remarks</Label>
              <Input data-testid="status-remarks-input" value={statusForm.remarks || ""}
                     onChange={(e) => setStatusForm((f) => ({ ...f, remarks: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="bg-white" onClick={() => setStatusRow(null)}>Cancel</Button>
            <Button onClick={changeStatus} data-testid="confirm-status-btn">Apply status change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!noteRow} onOpenChange={() => setNoteRow(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="note-dialog">
          <DialogHeader><DialogTitle>Add query / remark — {noteRow?.application_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Type</Label>
              <select className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm"
                      data-testid="note-type-select" value={noteForm.note_type}
                      onChange={(e) => setNoteForm((f) => ({ ...f, note_type: e.target.value }))}>
                {["Query", "Remark", "Document Request", "Follow-up"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Note</Label>
              <Input data-testid="note-input" value={noteForm.note}
                     onChange={(e) => setNoteForm((f) => ({ ...f, note: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="bg-white" onClick={() => setNoteRow(null)}>Cancel</Button>
            <Button onClick={addNote} data-testid="confirm-note-btn">Save note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
